import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeB24Frame } from '@bitrix24/b24jssdk';

type BitrixUser = {
  ID: string;
  NAME?: string;
  LAST_NAME?: string;
  EMAIL?: string;
  WORK_POSITION?: string;
  [key: string]: unknown;
};

type BitrixAuth = {
  access_token?: string;
  domain?: string;
  member_id?: string;
  user_id?: string | number;
  client_endpoint?: string;
  server_endpoint?: string;
  application_token?: string;
  refresh_token?: string;
  status?: string;
  expires_in?: number | string;
  expires?: number | string;
  [key: string]: unknown;
};

type AuthData = {
  access_token?: string;
  refresh_token?: string;
  expires?: number | string;
  expires_in?: number | string;
  domain?: string;
  member_id?: string;
  client_endpoint?: string;
  server_endpoint?: string;
  application_token?: string;
  status?: string;
  user_id?: string | number;
  [key: string]: unknown;
};

type BitrixContextValue = {
  user: BitrixUser | null;
  auth: string | null;
  dealId: string | null;
  loading: boolean;
  error: string | null;
  backendResponse: Record<string, unknown> | null;
  backendLoading: boolean;
  backendError: string | null;
};

const BitrixContext = createContext<BitrixContextValue | null>(null);

export const useBitrix = () => {
  const value = useContext(BitrixContext);
  if (!value) {
    throw new Error('useBitrix must be used inside BitrixProvider');
  }
  return value;
};

let _b24Instance: Awaited<ReturnType<typeof initializeB24Frame>> | null = null;
export const getB24 = () => _b24Instance;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'https://owen-api.tmg-din-reestr.ru';

async function verifyWithBackend(auth: BitrixAuth, userId: string, dealId: string) {
  const domain = typeof auth.domain === 'string' ? auth.domain : '';
  const response = await fetch(`${API_BASE_URL}/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Bitrix-Auth': auth.access_token ?? '',
      'X-Bitrix-Domain': domain,
    },
    body: JSON.stringify({
      user_id: userId,
      deal_id: dealId,
    }),
  });

  const payload = (await response.json().catch(() => null)) as
    | { detail?: string; status?: string; [key: string]: unknown }
    | null;

  if (!response.ok) {
    throw new Error(payload?.detail ?? `Backend rejected request with status ${response.status}`);
  }

  return payload ?? {};
}

function readPlacementDealId(b24: Awaited<ReturnType<typeof initializeB24Frame>>): string | null {
  const placement = b24.placement as
    | { options?: unknown; info?: () => unknown }
    | undefined;
  const candidates: unknown[] = [];

  const addOptionCandidates = (options: unknown) => {
    if (!options) {
      return;
    }

    if (typeof options === 'string') {
      const trimmed = options.trim();
      if (!trimmed) {
        return;
      }

      candidates.push(trimmed);
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        addOptionCandidates(parsed);
      } catch {
        return;
      }
      return;
    }

    if (typeof options !== 'object') {
      return;
    }

    const map = options as Record<string, unknown>;
    candidates.push(
      map.ID,
      map.id,
      map.dealId,
      map.deal_id,
      map.DEAL_ID,
      map.entityId,
      map.ENTITY_ID,
      map.IDS,
      map.ids,
      map.DEAL,
      map.deal,
    );
  };

  addOptionCandidates(placement?.options ?? null);

  const placementInfo = typeof placement?.info === 'function' ? placement.info() : null;
  if (placementInfo && typeof placementInfo === 'object') {
    const info = placementInfo as Record<string, unknown>;
    const infoOptions = info.options;
    addOptionCandidates(infoOptions ?? null);
  }

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) {
      continue;
    }
    const value = String(candidate).trim();
    if (value) {
      return value;
    }
  }

  const searchParams = new URLSearchParams(window.location.search);
  const rawPlacementOptions = searchParams.get('PLACEMENT_OPTIONS') ?? searchParams.get('placement_options');
  if (rawPlacementOptions) {
    try {
      const parsed = JSON.parse(decodeURIComponent(rawPlacementOptions)) as Record<string, unknown>;
      addOptionCandidates(parsed);
      for (const candidate of candidates) {
        if (candidate === null || candidate === undefined) {
          continue;
        }
        const value = String(candidate).trim();
        if (value) {
          return value;
        }
      }
    } catch {
      // ignore malformed placement payloads and fall through to query keys
    }
  }

  const queryKeys = ['dealId', 'deal_id', 'ID', 'id'];
  for (const key of queryKeys) {
    const value = searchParams.get(key);
    if (value && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

export const BitrixProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<BitrixUser | null>(null);
  const [auth, setAuth] = useState<string | null>(null);
  const [dealId, setDealId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backendResponse, setBackendResponse] = useState<Record<string, unknown> | null>(null);
  const [backendLoading, setBackendLoading] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    const init = async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          const mockUser: BitrixUser = {
            ID: '1',
            NAME: 'Dev',
            LAST_NAME: 'User',
            EMAIL: 'dev@example.com',
            WORK_POSITION: 'Developer',
          };
          const mockToken = `dev-mock-token-${Date.now()}`;

          if (!cancelled) {
            setUser(mockUser);
            setAuth(mockToken);
            setBackendResponse({
              status: 'development',
              user: mockUser,
              auth: mockToken,
            });
            setLoading(false);
          }
          return;
        }

        const b24 = await initializeB24Frame();
        _b24Instance = b24;

        if (!b24 || !b24.callMethod) {
          throw new Error('Bitrix SDK is not initialized');
        }

        const currentUserResult = await b24.callMethod('user.current', {});
        const currentUserPayload = currentUserResult?.getData?.() as
          | { result?: BitrixUser }
          | undefined;
        const currentUser = currentUserPayload?.result;
        if (!currentUser?.ID) {
          throw new Error('Failed to get Bitrix user');
        }

        const authData = b24.auth?.getAuthData?.() as AuthData | false | undefined;
        const accessToken = (authData as AuthData | undefined)?.access_token;
        if (!accessToken) {
          throw new Error('Failed to get Bitrix token');
        }

        const authPayload = authData as AuthData | undefined;
        const normalizedDealId = readPlacementDealId(b24);

        if (!normalizedDealId) {
          throw new Error('Failed to determine Bitrix deal ID from placement');
        }

        setBackendLoading(true);
        const backendResult = await verifyWithBackend(authPayload ?? {}, String(currentUser.ID), normalizedDealId);

        if (cancelled) {
          return;
        }

        setUser(currentUser);
        setAuth(accessToken);
        setDealId(normalizedDealId);
        setBackendResponse(backendResult as Record<string, unknown>);
        setBackendError(null);

        intervalId = setInterval(() => {
          const newToken = (b24.auth?.getAuthData?.() as AuthData | undefined)?.access_token;
          if (newToken && newToken !== accessToken) {
            setAuth(newToken);
          }
        }, 1000 * 60 * 4);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown Bitrix error';
        console.error('Bitrix initialization error:', err);
        if (!cancelled) {
          setError(message);
          setBackendError(message);
        }
      } finally {
        if (!cancelled) {
          setBackendLoading(false);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  return (
    <BitrixContext.Provider
      value={{ user, auth, dealId, loading, error, backendResponse, backendLoading, backendError }}
    >
      {children}
    </BitrixContext.Provider>
  );
};
